import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Badge, Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  TicketIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { supportAPI } from '../../services/api';

interface Ticket {
  id: number;
  ticket_id: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  message_count: number;
  last_reply_at: string;
  last_reply_by: string;
  customer_name: string | null;
  created_at: string;
  updated_at: string;
}

const statusColors: Record<string, { bg: string; text: string }> = {
  open: { bg: '#dcfce7', text: '#065f46' },
  in_progress: { bg: '#fef3c7', text: '#92400e' },
  waiting: { bg: '#fce7f3', text: '#9d174d' },
  resolved: { bg: '#d1fae5', text: '#065f46' },
  closed: { bg: '#f3f4f6', text: '#6b7280' },
};

const priorityColors: Record<string, { bg: string; text: string }> = {
  low: { bg: '#f0fdf4', text: '#166534' },
  medium: { bg: '#fffbeb', text: '#92400e' },
  high: { bg: '#fef2f2', text: '#991b1b' },
  urgent: { bg: '#fef2f2', text: '#dc2626' },
};

const categoryLabels: Record<string, string> = {
  general: 'General',
  billing: 'Billing',
  technical: 'Technical',
  account: 'Account',
  feature: 'Feature Request',
  bug: 'Bug Report',
  other: 'Other',
};

const statusLabels: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting: 'Waiting',
  resolved: 'Resolved',
  closed: 'Closed',
};

const SupportTickets: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const res = await supportAPI.getTickets();
      setTickets(res.data);
    } catch {
      console.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const filtered = tickets.filter(t => {
    const matchSearch = !search ||
      t.subject.toLowerCase().includes(search.toLowerCase()) ||
      t.ticket_id.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const openCount = tickets.filter(t => ['open', 'in_progress', 'waiting'].includes(t.status)).length;
  const resolvedCount = tickets.filter(t => t.status === 'resolved').length;
  const closedCount = tickets.filter(t => t.status === 'closed').length;

  const formatDate = (d: string) => {
    const date = new Date(d);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <CompanyLayout>
      <Container fluid className="px-3 px-lg-4 page-content-padding" style={{ paddingTop: '24px' }}>
        {/* Header */}
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <TicketIcon style={{ width: '24px', height: '24px', color: '#065f46' }} />
              <h4 className="mb-0" style={{ fontWeight: 700, color: '#0f172a' }}>Support</h4>
            </div>
            <p className="mb-0" style={{ color: '#64748b', fontSize: '0.88rem' }}>
              Get help from our team — we typically respond within a few hours.
            </p>
          </div>
          <Button
            onClick={() => navigate('/company/support/new')}
            className="d-flex align-items-center gap-2 align-self-start"
            style={{
              borderRadius: '10px',
              padding: '10px 20px',
              fontWeight: 600,
              fontSize: '0.9rem',
              background: 'linear-gradient(135deg, #065f46, #059669)',
              color: 'white',
              border: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <PlusIcon style={{ width: '18px', height: '18px' }} />
            New Ticket
          </Button>
        </div>

        {/* Stats Cards */}
        <Row className="g-2 g-md-3 mb-4">
          {[
            { label: 'Active', count: openCount, color: '#065f46', bg: '#dcfce7' },
            { label: 'Resolved', count: resolvedCount, color: '#065f46', bg: '#d1fae5' },
            { label: 'Closed', count: closedCount, color: '#6b7280', bg: '#f3f4f6' },
            { label: 'Total', count: tickets.length, color: '#374151', bg: '#f1f5f9' },
          ].map((s) => (
            <Col xs={3} md={3} key={s.label}>
              <div className="mobile-stat-card" style={{
                background: 'white',
                borderRadius: '12px',
                padding: '12px',
                border: '1px solid #e2e8f0',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500, marginBottom: '2px' }}>{s.label}</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: s.color }}>{s.count}</div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Filters */}
        <div className="d-flex flex-column flex-md-row gap-2 mb-3">
          <InputGroup style={{ maxWidth: '320px' }}>
            <InputGroup.Text style={{ background: 'white', borderRight: 'none', borderColor: '#e2e8f0' }}>
              <MagnifyingGlassIcon style={{ width: '16px', height: '16px', color: '#94a3b8' }} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search tickets..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ borderLeft: 'none', borderColor: '#e2e8f0', fontSize: '0.88rem' }}
            />
          </InputGroup>
          <div className="d-flex align-items-center gap-2">
            <FunnelIcon style={{ width: '16px', height: '16px', color: '#94a3b8' }} />
            <Form.Select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ width: 'auto', fontSize: '0.88rem', borderColor: '#e2e8f0', borderRadius: '8px' }}
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="waiting">Waiting</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </Form.Select>
          </div>
        </div>

        {/* Ticket List */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="success" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-5" style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <TicketIcon style={{ width: '48px', height: '48px', color: '#cbd5e1', marginBottom: '12px' }} />
            <p style={{ color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
              {tickets.length === 0 ? 'No tickets yet' : 'No matching tickets'}
            </p>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
              {tickets.length === 0 ? 'Create a support ticket to get help from our team.' : 'Try adjusting your search or filter.'}
            </p>
            {tickets.length === 0 && (
              <Button
                size="sm"
                onClick={() => navigate('/company/support/new')}
                style={{ borderRadius: '8px', background: '#065f46', border: 'none', fontWeight: 600 }}
              >
                Create Ticket
              </Button>
            )}
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            {filtered.map((ticket, idx) => (
              <div
                key={ticket.id}
                role="button" tabIndex={0}
                onClick={() => navigate(`/company/support/${ticket.id}`)}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && navigate(`/company/support/${ticket.id}`)}
                style={{
                  padding: '16px 20px',
                  borderBottom: idx < filtered.length - 1 ? '1px solid #f1f5f9' : 'none',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.92rem' }}>{ticket.subject}</span>
                    <span style={{
                      background: statusColors[ticket.status]?.bg || '#f3f4f6',
                      color: statusColors[ticket.status]?.text || '#6b7280',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      padding: '3px 8px',
                      borderRadius: '6px',
                    }}>
                      {statusLabels[ticket.status] || ticket.status}
                    </span>
                    <span style={{
                      background: priorityColors[ticket.priority]?.bg || '#f3f4f6',
                      color: priorityColors[ticket.priority]?.text || '#6b7280',
                      fontWeight: 500,
                      fontSize: '0.68rem',
                      padding: '2px 6px',
                      borderRadius: '4px',
                    }}>
                      {ticket.priority}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                    <span>{ticket.ticket_id}</span>
                    <span>{categoryLabels[ticket.category] || ticket.category}</span>
                    <span className="d-flex align-items-center gap-1">
                      <ChatBubbleLeftRightIcon style={{ width: '12px', height: '12px' }} />
                      {ticket.message_count}
                    </span>
                    <span className="d-flex align-items-center gap-1">
                      <ClockIcon style={{ width: '12px', height: '12px' }} />
                      {formatDate(ticket.last_reply_at || ticket.updated_at)}
                    </span>
                    {ticket.last_reply_by === 'admin' && ticket.status !== 'closed' && (
                      <span style={{
                        background: '#ecfdf5',
                        color: '#065f46',
                        fontSize: '0.68rem',
                        fontWeight: 600,
                      }}>
                        Staff replied
                      </span>
                    )}
                    {ticket.customer_name && (
                      <span style={{
                        background: '#f1f5f9',
                        color: '#475569',
                        fontSize: '0.68rem',
                        fontWeight: 600,
                      }}>
                        From: {ticket.customer_name}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRightIcon style={{ width: '16px', height: '16px', color: '#cbd5e1', flexShrink: 0 }} />
              </div>
            ))}
          </div>
        )}
      </Container>
    </CompanyLayout>
  );
};

export default SupportTickets;
